// ─── Pending print-originals, persisted in IndexedDB ─────────────────────────
// The background 3600px original upload used to live ONLY in tab memory
// (originalsRef + held File objects): a refresh, tab-kill, or iOS memory
// eviction lost the bytes forever and the photo could never reach print.
// This store keeps each not-yet-confirmed original's SOURCE bytes on disk so
// a returning tab can rebuild the File and resume the upload.
//
// PERF REWORK (2026-07-05 — "uploads got slow"): v1 stored an ArrayBuffer in
// a single store and read EVERYTHING back (getAll) on session load. That
// meant (a) a full main-thread read of every source file DURING the upload
// batch (arrayBuffer() copy), and (b) loading hundreds of MB of stale bytes
// at page open (gc + resume both getAll'd payloads). Both competed with the
// live uploads. v2:
//   - TWO stores: 'meta' (tiny records — listing/GC never touches bytes) and
//     'payloads' (the Blob, stored AS-IS: no arrayBuffer() copy on save).
//   - Payloads are fetched ONE photo at a time, only when a resume actually
//     needs them.
//   - Callers persist during browser idle time (see AlbumFlowPage), never on
//     the upload hot path.
// Everything stays best-effort: on quota/IDB failure the upload path behaves
// exactly as before (in-memory only) and the Fix-1 submit gate still blocks
// any silent loss.

const DB_NAME = 'storiz-pending-originals';
const META_STORE = 'meta';
const PAYLOAD_STORE = 'payloads';

export interface PendingOriginalMeta {
  /** Key — the photo row's id (stable across refreshes). */
  photoId: string;
  sessionId: string;
  name: string;
  type: string;
  lastModified: number;
  /** Signed-URL PUT fallback target (may be expired on resume — caller
   *  re-mints via /photos/:id/overwrite-url before retrying). */
  uploadUrl: string;
  /** TUS block (bucket/object_path/host); tokens are refreshed per-request. */
  tus: unknown | null;
  savedAt: number;
}

/** Blob in production (zero-copy save); ArrayBuffer kept as an accepted type
 *  for environments whose IDB can't round-trip Blobs (and for tests). */
export type PendingPayload = Blob | ArrayBuffer;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      // v1 kept bytes inline in 'originals' — drop it (day-old pendings are
      // covered by the server-truth submit gate) and split meta/payloads.
      if (db.objectStoreNames.contains('originals')) db.deleteObjectStore('originals');
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'photoId' });
      }
      if (!db.objectStoreNames.contains(PAYLOAD_STORE)) {
        db.createObjectStore(PAYLOAD_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  stores: string[],
  mode: IDBTransactionMode,
  run: (t: IDBTransaction) => IDBRequest<T> | void,
): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const t = db.transaction(stores, mode);
        let result: T | undefined;
        const req = run(t);
        if (req) {
          req.onsuccess = () => { result = req.result; };
          req.onerror = () => reject(req.error);
        }
        t.oncomplete = () => { db.close(); resolve(result); };
        t.onabort = () => { db.close(); reject(t.error); };
        t.onerror = () => { /* surfaced via abort */ };
      }),
  );
}

/** Upsert one pending original: tiny meta record + the payload Blob stored
 *  as-is (no byte copy). Swallows quota/IDB errors — persistence is a safety
 *  net, never a reason to slow or fail the upload itself. */
export async function savePendingOriginal(
  meta: PendingOriginalMeta,
  payload: PendingPayload,
): Promise<void> {
  try {
    await tx([META_STORE, PAYLOAD_STORE], 'readwrite', (t) => {
      t.objectStore(META_STORE).put(meta);
      t.objectStore(PAYLOAD_STORE).put(payload, meta.photoId);
    });
  } catch { /* quota / private mode — in-memory path still works */ }
}

export async function deletePendingOriginal(photoId: string): Promise<void> {
  try {
    await tx([META_STORE, PAYLOAD_STORE], 'readwrite', (t) => {
      t.objectStore(META_STORE).delete(photoId);
      t.objectStore(PAYLOAD_STORE).delete(photoId);
    });
  } catch { /* ignore */ }
}

/** Metadata only — NEVER loads payload bytes. Safe to call on session load. */
export async function listPendingOriginals(sessionId: string): Promise<PendingOriginalMeta[]> {
  try {
    const all = await tx<PendingOriginalMeta[]>([META_STORE], 'readonly', (t) =>
      t.objectStore(META_STORE).getAll() as IDBRequest<PendingOriginalMeta[]>);
    return (all ?? []).filter((e) => e.sessionId === sessionId);
  } catch {
    return [];
  }
}

/** Fetch ONE photo's source bytes — only called when a resume genuinely
 *  needs to re-upload that photo. */
export async function getPendingPayload(photoId: string): Promise<PendingPayload | null> {
  try {
    const v = await tx<PendingPayload>([PAYLOAD_STORE], 'readonly', (t) =>
      t.objectStore(PAYLOAD_STORE).get(photoId) as IDBRequest<PendingPayload>);
    return v ?? null;
  } catch {
    return null;
  }
}

/** Drop entries older than maxAgeMs (default 7 days) across ALL sessions.
 *  Reads META only — stale multi-MB payloads are deleted by key, never read. */
export async function gcPendingOriginals(maxAgeMs = 7 * 24 * 3600_000): Promise<void> {
  try {
    const all = await tx<PendingOriginalMeta[]>([META_STORE], 'readonly', (t) =>
      t.objectStore(META_STORE).getAll() as IDBRequest<PendingOriginalMeta[]>);
    const cutoff = Date.now() - maxAgeMs;
    const stale = (all ?? []).filter((e) => (e.savedAt ?? 0) < cutoff);
    for (const e of stale) await deletePendingOriginal(e.photoId);
  } catch { /* ignore */ }
}

/** Test helper / hard reset. */
export async function clearAllPendingOriginals(): Promise<void> {
  try {
    await tx([META_STORE, PAYLOAD_STORE], 'readwrite', (t) => {
      t.objectStore(META_STORE).clear();
      t.objectStore(PAYLOAD_STORE).clear();
    });
  } catch { /* ignore */ }
}
